import { useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";

import { TableLoader, DataGrid, TableSearchAndFilters, TableContainer } from "ui/data-grid";

import { useInboxReviews } from "hooks/api/use-inbox-reviews";
import { useGetAudienceTypesQuery } from "hooks/api/dictionary";

import { createApprovedTableColumns } from "containers/client/approved/approved-table-columns";
import FiltersOnClientReviewsTable from "containers/client/components/client-reviews-table/filters-on-client-reviews-table";
import {
  CLIENT_REVIEWS_FILTER_ID_TO_TITLE,
  CLIENT_REVIEWS_FILTER_VALUE_TO_LABEL,
  CLIENT_REVIEWS_SEARCH_PLACEHOLDER,
  CLIENT_TABLE_CONFIG_KEYS,
  DEFAULT_CLIENT_REVIEWS_FILTERS,
} from "containers/client/components/client-reviews-table/client-reviews-table.consts";
import { useClientTableColumnLayout } from "containers/client/hooks/use-client-table-column-layout";

import { InboxFilter } from "types/inbox";
import { TouchpointStatuses } from "types";
import { FiltersToApplyOnClientReviewsTable } from "containers/client/components/client-reviews-table/client-reviews-table.types";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";

const APPROVED_REVIEW_STATUSES = [TouchpointStatuses.PUBLISHED, TouchpointStatuses.APPROVED];

const ApprovedPage = () => {
  const [filtersToApply, setFiltersToApply] =
    useState<FiltersToApplyOnClientReviewsTable>(DEFAULT_CLIENT_REVIEWS_FILTERS);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "lastModifiedDate", desc: true },
  ]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { data: audienceDictionaryItems = [] } = useGetAudienceTypesQuery();

  const handleSearchTermChange = (searchTerm: string) => {
    setPageIndex(0);
    setCurrentSearchTerm(searchTerm);
  };

  const handleFiltersChange = (next: FiltersToApplyOnClientReviewsTable) => {
    setFiltersToApply(next);
    setPageIndex(0);
  };

  const handleAmountChange = ({ pageSize: nextPageSize }: { pageSize: number }) => {
    setPageSize(nextPageSize);
    setPageIndex(0);
  };

  const inboxFilters: InboxFilter = {
    touchpointVersionStatus: APPROVED_REVIEW_STATUSES,
    touchpointType: filtersToApply.type?.length ? filtersToApply.type : [],
    populationType: filtersToApply.populationType?.length ? filtersToApply.populationType : [],
    q: currentSearchTerm,
  };

  const { data: reviewsData, isLoading } = useInboxReviews({
    page: pageIndex,
    size: pageSize,
    sort: sortingParams.map((param) => `${param.id},${param.desc ? "desc" : "asc"}`).join(","),
    filters: inboxFilters,
  });

  const currentSearchResults = reviewsData?.items;
  const totalRecords = reviewsData?.totalElements ?? 0;
  const pagesAmount = reviewsData?.totalPages ?? 0;

  const allColumns = useMemo(
    () =>
      createApprovedTableColumns({
        audienceDictionaryItems,
        hasTemplates:
          currentSearchResults?.some((tp) => tp.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME) ??
          false,
      }),
    [audienceDictionaryItems, currentSearchResults],
  );

  const { tableColumnsConfig, columnOptions, visibleColumns, handleToggleColumn } =
    useClientTableColumnLayout({
      tableConfigKey: CLIENT_TABLE_CONFIG_KEYS.approvedCreatives,
      allColumns,
      primaryColumnIds: ["touchpointName"],
    });

  const isSearchOrFiltering = Boolean(
    currentSearchTerm || filtersToApply.type?.length || filtersToApply.populationType?.length,
  );

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden bg-gray-100">
      <div className="shrink-0 pt-3 pl-3 pr-5 pb-4">
        <div className="mb-4 flex h-5 items-center">
          <p className="text-sm font-semibold leading-5 text-gray-900">Approved creatives</p>
        </div>
        <TableSearchAndFilters
          setCurrentSearchTerm={handleSearchTermChange}
          placeholder={CLIENT_REVIEWS_SEARCH_PLACEHOLDER}
          filtersToApply={filtersToApply}
          setFiltersToApply={handleFiltersChange}
          defaultFiltersToApply={DEFAULT_CLIENT_REVIEWS_FILTERS}
          filterIdToTitle={CLIENT_REVIEWS_FILTER_ID_TO_TITLE}
          filterValueToLabel={CLIENT_REVIEWS_FILTER_VALUE_TO_LABEL}
          tableFilters={
            <FiltersOnClientReviewsTable
              filtersToApply={filtersToApply}
              setFiltersToApply={handleFiltersChange}
              tableConfigKey={CLIENT_TABLE_CONFIG_KEYS.approvedCreatives}
              tableColumnsConfig={tableColumnsConfig}
              columnOptions={columnOptions}
              onToggleColumn={handleToggleColumn}
              isFetching={isLoading}
            />
          }
        />
      </div>

      <div className="min-h-0 flex-1 w-full overflow-y-auto custom-scroll">
        <div className="pl-3 pr-5 pb-5">
          <TableContainer className="mb-0">
            <TableLoader isLoading={isLoading} fullScreen />

            {!isLoading &&
              (currentSearchResults?.length ? (
                <DataGrid
                  columns={visibleColumns}
                  data={currentSearchResults}
                  isLoading={isLoading}
                  manualSorting={false}
                  showingRecordsIsOn={true}
                  viewByAmountSelectIsOn={true}
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  pagesAmount={pagesAmount}
                  setAmount={handleAmountChange}
                  pageIndex={pageIndex}
                  setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
                  sortingParams={sortingParams}
                  setSortingParams={setSortingParams}
                />
              ) : isSearchOrFiltering ? (
                <p className="text-gray-500">No results found</p>
              ) : (
                <p className="text-gray-500">There are no approved creatives yet</p>
              ))}
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default ApprovedPage;
